"use client";
import Image from "next/image";
import HighlightedText from "./HighlightedText";
import { getStrapiMedia } from "../lib/api-helpers";
import { useEffect, useLayoutEffect } from "react";
import ReactGA from "react-ga4";

interface Button {
  id: string;
  url: string;
  text: string;
  type: string;
  newTab: boolean;
}

interface Picture {
  data: {
    id: string;
    attributes: {
      url: string;
      name: string;
      alternativeText: string;
    };
  };
}

interface HeroProps {
  data: {
    id: string;
    titleHero: string;
    arcDirection: string;
    arcColor: string;
    descriptionHero: string;
    picture: Picture;
    buttons: Button[];
  };
}

export default function Hero({ data }: HeroProps) {
  const imgUrl = getStrapiMedia(data?.picture?.data?.attributes?.url);

  function addStyle() {
    const htmlElement = document.documentElement;

    htmlElement.style.scrollSnapType = "y mandatory";
    htmlElement.style.scrollPaddingTop = "65px";
    htmlElement.style.scrollBehavior = "smooth";
    htmlElement.style.scrollSnapStop = "always";
  }

  function removeStyle() {  
    const htmlElement = document.documentElement;

    htmlElement.style.scrollSnapType = "";
    htmlElement.style.scrollPaddingTop = "";
    htmlElement.style.scrollBehavior = "";
    htmlElement.style.scrollSnapStop = "";
  }

  const getItemArrangment = (alt: any) => {
    switch (alt) {
      case "center":
        return "flex items-center justify-center flex-col items-end overflow-hidden height-100 pt-[48px] xl:pt-[120px]";

      case "left":
        return "flex items-center justify-start flex-col md:flex-row items-center pt-[48px] pb-[36px] md:py-[96px]";

      default:
        return "w-12 min-w-12  h-12  4xl:w-24 4xl:h-24";
    }
  };

  useLayoutEffect(() => {
    return () => {
      window.scrollTo(0, 0);
      removeStyle();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const isTop = window.scrollY === 0;
      const isBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight;

      if (isTop || isBottom) {
        removeStyle();
      } else {
        addStyle();
      }
    };

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll);

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  useEffect(() => {
    const timeOut = setTimeout(() => {
      const measurementId = process.env.NEXT_PUBLIC_MEASUREMENT_ID;
      measurementId ? ReactGA.initialize(measurementId) : "";
      ReactGA.send({
        hitType: "pageview",
        page: "/qualitia-for-web-and-mobile",
        title: "Default",
      });
    }, 1000);
    return () => clearTimeout(timeOut);
  }, []);

  return (
    <section className='flex flex-col items-center justify-center text-gray-600 body-font height-100 '>
      <h2 className='sr-only'>Hero Section</h2>
      <div
        className={`container relative mx-auto xl:px-0 px-6 ${getItemArrangment(
          data.arcDirection
        )}`}>
        <div
          className={`relative z-50 w-full ${
            data.arcDirection === "left"
              ? "text-left flex flex-col items-start self-center xl:col-span-2"
              : "text-center"
          }`}>
          <HighlightedText
            text={data.titleHero}
            tag='h1'
            className={`!leading-[1.2] text-5xl 2xl:text-6xl mb-[16px]  w-full font-bold  text-[#032d60]`}
          />
          <HighlightedText
            text={data.descriptionHero}
            tag='p'
            className={`text-2xl text-[#032d60] ${
              data.arcDirection === "left" ? "" : "mx-auto max-w-[360px] lg:max-w-full mb-[56px]"
            }`}
          />
        </div>
        <div
          className={`relative mt-[48px] ${
            data.arcDirection === "left" ? "relative lg:mt-[0px] w-full max-w-[320px] md:max-w-[400px] lg:max-w-[380px] xl:max-w-[360px]  3xl:max-w-[480px] xl:right-[50px] 2xl:right-[0px] 3xl:right-[50px]" : "mt-auto"
          }`}>
          <Image
            src={imgUrl ?? ""}
            alt={
              data.picture?.data?.attributes?.alternativeText || "none provided"
            }
            className={`mt-auto w-full  relative z-50 object-contain object-center ${
              data.arcDirection === "left"
                ? "w-full md:h-auto lg:h-[450px] xl:h-[420px]"
                : "md:h-auto lg:h-[500px] xl:h-[480px]"
            }`}
            width={480}
            height={480}
            priority={true}
            fetchPriority="high"
          />
        </div>
      </div>
    </section>
  );
}
